import type { PushDiagnosticEntry, PushDiagnosticStage } from "./plugin";

/** Every entry recorded for one notification, oldest first. */
export interface PushTrail {
  notificationId: string;
  stages: PushDiagnosticStage[];
  entries: PushDiagnosticEntry[];
  /** Milliseconds from the first entry to the last, or undefined if only one. */
  spanMs?: number;
}

export type PushAnomalyKind =
  /** Capacitor's router never saw a notification the pre-bridge delegate handled. */
  | "legacyDelegateIntercepted"
  /** Foregrounded, but iOS was told to display nothing. */
  | "foregroundSuppressed"
  /** The service extension ran out of its ~30s budget. */
  | "serviceExtensionExpired"
  /** Tapped, but the bridge was gone, so the webview was never told. */
  | "bridgeMissingOnTap"
  /** Registration for remote notifications failed: no pushes will arrive at all. */
  | "tokenFailed";

export interface PushAnomaly {
  kind: PushAnomalyKind;
  notificationId: string;
  detail: string;
}

/**
 * Parses the NDJSON lines the native side hands over, discarding any line that is not
 * a usable entry. A malformed line means one lost step, never a lost report.
 */
export const parseEntries = (lines: string[]): PushDiagnosticEntry[] => {
  const entries: PushDiagnosticEntry[] = [];
  for (const line of lines) {
    try {
      const parsed: unknown = JSON.parse(line);
      if (parsed && typeof parsed === "object" && typeof (parsed as PushDiagnosticEntry).stage === "string") {
        entries.push(parsed as PushDiagnosticEntry);
      }
    } catch {
      // A torn write; the surrounding entries are still good.
    }
  }
  return entries;
};

/**
 * Groups by notification so a trail can be read as a journey. Entries with no
 * notificationId (the token stages) are grouped under "" and reported as one trail,
 * which keeps them in the report without inventing an association.
 */
export const groupByNotification = (entries: PushDiagnosticEntry[]): PushTrail[] => {
  const byId = new Map<string, PushDiagnosticEntry[]>();
  for (const entry of entries) {
    const id = entry.notificationId ?? "";
    const existing = byId.get(id);
    if (existing) {
      existing.push(entry);
    } else {
      byId.set(id, [entry]);
    }
  }

  return [...byId].map(([notificationId, trailEntries]) => {
    const sorted = [...trailEntries].sort((a, b) => a.at.localeCompare(b.at));
    return {
      notificationId,
      stages: sorted.map((entry) => entry.stage),
      entries: sorted,
      spanMs: span(sorted),
    };
  });
};

/** Undefined unless the trail holds at least two parseable timestamps. */
const span = (entries: PushDiagnosticEntry[]): number | undefined => {
  const times = entries.map((entry) => Date.parse(entry.at)).filter((time) => !isNaN(time));
  return times.length > 1 ? Math.max(...times) - Math.min(...times) : undefined;
};

/**
 * The failure modes worth waking someone up for. Deliberately not "arrived but never
 * tapped" — that is the overwhelmingly common, entirely correct case, and reporting it
 * would bury the real signals.
 */
export const findAnomalies = (trails: PushTrail[]): PushAnomaly[] => {
  const anomalies: PushAnomaly[] = [];

  for (const trail of trails) {
    const { notificationId, stages, entries } = trail;

    if (stages.includes("legacyAppDelegate") && !stages.includes("didReceive")) {
      anomalies.push({
        kind: "legacyDelegateIntercepted",
        notificationId,
        detail: "handled before the Capacitor bridge loaded, so the webview was never notified",
      });
    }

    if (stages.includes("serviceExtensionExpired")) {
      anomalies.push({
        kind: "serviceExtensionExpired",
        notificationId,
        detail: "the service extension exceeded its time budget",
      });
    }

    for (const entry of entries) {
      if (entry.stage === "willPresent" && entry.suppressed === true) {
        anomalies.push({
          kind: "foregroundSuppressed",
          notificationId,
          detail: `shown with no presentation options (reason: ${String(entry.presentationReason)})`,
        });
      }
      if (entry.stage === "didReceive" && entry.hasPlugin === false) {
        anomalies.push({
          kind: "bridgeMissingOnTap",
          notificationId,
          detail: "tapped while the push plugin had no bridge attached",
        });
      }
      if (entry.stage === "tokenFailed") {
        anomalies.push({
          kind: "tokenFailed",
          notificationId,
          detail: `registration failed: ${String(entry.error)}`,
        });
      }
    }
  }

  return anomalies;
};

/** One line per trail, for a Sentry breadcrumb or a log. */
export const summariseTrail = (trail: PushTrail): string => {
  const id = trail.notificationId || "(no notification)";
  const span = trail.spanMs === undefined ? "" : ` in ${trail.spanMs}ms`;
  return `${id}: ${trail.stages.join(" → ")}${span}`;
};
